import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { first, map, Observable, of, startWith } from "rxjs";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { Country, State, City } from "country-state-city";
import { MatStep, MatStepper } from "@angular/material/stepper";
import { HttpErrorResponse } from "@angular/common/http";
import { Admin } from "../../services/admin";
import { CustomValidators } from "../../validators/customValidators";
import { Auth } from "../../services/auth";
import { SuccessDialog } from "../../pages/common-features/success-dialog/success-dialog";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Optional, Inject } from "@angular/core";
import { Help } from "../../pages/common-features/help/help";

declare const google: any;
const USER_KEY = "user";

@Component({
  selector: "app-registration",
  standalone: false,
  templateUrl: "./registration.html",
  styleUrl: "./registration.less",
})
export class Registration implements OnInit, AfterViewInit {
  firstFormGroup!: FormGroup;
  secondFormGroup!: FormGroup;
  submitted: boolean = false;
  countryCode: any = 'us';
  loading: boolean = false;
  showError: boolean = false;
  countries=Country.getAllCountries();
  ssoResponse: any;
  orgTypes: any = [];
  accountRegistrationDone: boolean = false;
  currentStep: 1 | 2 | 3 = 1;
  status: string = '';
  showPassword: boolean = false;
  message: any;
  filteredCountries$!: Observable<any[]>;
  filteredCountries2$!: Observable<any[]>;
  isModal = false;
  userType: string = '';

  passwordErrorMessages: any = {
    hasNumber: 'Password must contain a number.',
    hasCapitalCase: 'Password must contain an uppercase letter.',
    hasSmallCase: 'Password must contain a lowercase letter.',
    minlength: 'Password must be at least 8 characters long.',
  };

  constructor(
    private formBuilder: FormBuilder,
    private adminService: Admin,
    private authService: Auth,
    private _dialog: MatDialog,
    public router: Router,
    private dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<Registration>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isModal = !!this.dialogRef;
  }

  ngOnInit(): void {
    this.getOrgTypes();
    this.initForms();
    if (this.data && this.data.userType) {
      const persona = this.data.userType.replace('_admin', '').toLowerCase();
      this.firstFormGroup.patchValue({ primaryPersona: persona });
    }
  }

  ngAfterViewInit(): void {
    this.initGoogleSSO();
  }

 

  private initForms() {
    this.firstFormGroup = this.formBuilder.group({
      region: ["", Validators.required],
      primaryPersona: ["buyer", Validators.required],
      category: [null, Validators.required],
      orgSubType: [],
      termsAccepted: [false, Validators.requiredTrue]
    });

    this.filteredCountries$ = this.firstFormGroup.get('region')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCountries(value || ''))
    );


    // Handle conditional validation for category and orgSubType
    this.firstFormGroup.get('primaryPersona')?.valueChanges.subscribe(val => {
      const categoryControl = this.firstFormGroup.get('category');
      const orgSubTypeControl = this.firstFormGroup.get('orgSubType');

      if (val === 'investor') {
        categoryControl?.clearValidators();
        orgSubTypeControl?.setValidators([Validators.required]);
      } else {
        categoryControl?.setValidators([Validators.required]);
        orgSubTypeControl?.clearValidators();
      }

      categoryControl?.updateValueAndValidity();
      orgSubTypeControl?.updateValueAndValidity();
    });

    this.secondFormGroup = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      companyName: ["", Validators.required],
      emailId: ["", [Validators.pattern(/^[\w\-.]+@([\w-]+\.)+[\w-]{2,}$/), Validators.required]],
      password: [
        "",
        Validators.compose([
          Validators.required,
          CustomValidators.patternValidator(/\d/, { hasNumber: true }),
          CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
          CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }),
          Validators.minLength(8),
        ]),
      ],
      phoneNumber: ["", [Validators.required]],
      countryCode: ['', Validators.required],
      confirmPassword: ["", Validators.required],
    }, { validators: CustomValidators.passwordMatchValidator });

    this.filteredCountries2$ = this.secondFormGroup.get('countryCode')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCountriesCode(value || ''))
    );

  }

  handleBack() {
    if (this.isModal) {
      // Custom back logic for modal (e.g., previous step in a form wizard)
      console.log('Modal back clicked');
    } else {
      // Standard browser/route history back
      this.dialogRef.close(false);
    }
  }

  closeModal() {
    if (this.isModal) {
      this.dialogRef.close(false); // pass false or data back
    }
  }

  private initGoogleSSO() {
    (window as any).handleCredentialResponse = (response: any) => {
      const idToken = response.credential;
      const formData = { token: idToken, source: 'GOOGLE' };
      this.adminService.ssoLogin(formData).subscribe({
        next: (data: any) => {
          this.ssoResponse = data;
          if (data && data.registered == false) {
            this.secondFormGroup.patchValue({ emailId: data.email });
          } else if (data && data.jwtToken) {
            this.authService.updateAppUser(data);
            this.router.navigate(['/home']);
          }
        }
      });
    };

    (window as any).google?.accounts.id.renderButton(
      document.getElementById('google-signin-button'),
      { theme: 'outline', size: 'large' }
    );
  }

  get secondFormControls() {
    return this.secondFormGroup.controls;
  }

  goBack() {
    if (this.currentStep > 1) {
      this.currentStep = (this.currentStep - 1) as 1 | 2 | 3;
    }
  }

  goStep(step: number) {
    this.currentStep = step as 1 | 2 | 3;
  }

  validateStep1() {
    console.log('Validating Step 1:', this.firstFormGroup.value);
    console.log('Is valid?', this.firstFormGroup.valid);
    if (this.firstFormGroup.valid) {
      this.currentStep = 2;
    } else {
      Object.keys(this.firstFormGroup.controls).forEach(key => {
        this.firstFormGroup.get(key)?.markAsTouched();
      });
    }
  }

  validateStep2() {
    if (this.secondFormGroup.valid) {
      this.loading = true;
      this.onSubmit().subscribe({
        next: () => {
          this.accountRegistrationDone = true;
          this.loading = false;
          this.status = 'success';
          this.showSuccessDialogComponent('Welcome to Agrify AI ', 'You have successfully registered.', 'success');
          // this.currentStep = 3;
        },
        error: (err: HttpErrorResponse) => {
          this.accountRegistrationDone = false;
          this.loading = false;
          // this.currentStep = 3;
          this.status = 'error';
          this.message = err.error || err;
          console.log(this.message);
          const status = this.message?.status;

          if (status && typeof status === 'string' && status.includes('Duplicate')) {
            this.message = 'Account already exists.';
          } else  {
            this.message = 'Error registering user. Please contact admin.';
          }

          this.showSuccessDialogComponent('Oops something went wrong', this.message, 'error');
        }
      });
    } else {
      Object.keys(this.secondFormGroup.controls).forEach(key => {
        this.secondFormGroup.get(key)?.markAsTouched();
      });
    }
  }

  selectRole(role: string) {
    this.firstFormGroup.patchValue({ primaryPersona: role });
  }

  selectSub(sub: any, persona: string) {
    let value = typeof sub === 'string' ? sub : sub.innerText || sub;
    if (persona === 'investor') {
      this.firstFormGroup.patchValue({ orgSubType: value });
    } else {
      this.firstFormGroup.patchValue({ category: value.includes('Globally') || value.includes('Export') });
    }
  }

  private _filterCountries(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.countries.filter(country => country.name.toLowerCase().includes(filterValue));
  }


  private _filterCountriesCode(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.countries.filter(country => country.name.toLowerCase().includes(filterValue) || country.phonecode.includes(filterValue));
  }

  onCountryChange(event?: any) {
    const countryName = event?.target?.value || event;
    const country = this.countries.find(c => c.name === countryName);
    if (country) {
      this.firstFormGroup.patchValue({ region: country.name });
      this.secondFormGroup.patchValue({ countryCode: country.phonecode });
      this.countryCode = country.isoCode.toLowerCase();
    }
  }

  onCountryCodeChange(event?: any) {
    const countryName = event?.target?.value || event;
    const country = this.countries.find(c => c.name === countryName);
    if (country) {
      this.secondFormGroup.patchValue({ countryCode: country.phonecode });
      this.countryCode = country.isoCode.toLowerCase();
    }
  }

  getOrgTypes() {
    this.adminService.getOrgTypes().subscribe({
      next: (data: any) => {
        this.orgTypes = data;
      }
    });
  }

  displayFn(country: any) {
    return country ? country.name : country;
  }

  onSubmit(): Observable<any> {
    this.submitted = true;
    const persona = this.firstFormGroup.value.primaryPersona.toUpperCase();
    const orgType = this.orgTypes.find((x: any) => x.type === persona + "_ADMIN") || this.orgTypes[0];

    const data = {
      "name": this.secondFormGroup.value.companyName,
      "addLine1": "", "addLine2": "", "city": "", "state": "",
      "country": this.firstFormGroup.value.region,
      "zipcode": "", "website": "", "apiKey": "", "apiEnabled": true,
      "deleted": false, "dateCreated": new Date(), "createdBy": "",
      "corpMaxCoverage": 0, "homeMaxCoverage": 0, "uomDistance": "",
      "connectorClassName": "", "locationCapture": 0, "osrmServerHost": "",
      "osrmServerPort": 0, "defaultOrg": true, "companyId": "",
      "orgType": orgType,
      "user": {
        "emailId": this.secondFormGroup.value.emailId || '',
        "firstName": this.secondFormGroup.value.firstName,
        "lastName": this.secondFormGroup.value.lastName,
        "userName": this.secondFormGroup.value.companyName,
        "passwd": this.secondFormGroup.value.password,
        "countryCode": this.secondFormGroup.value.countryCode.includes("+") ?this.secondFormGroup.value.countryCode.split("+")[1] : this.secondFormGroup.value.countryCode,
        "mobilePhone": this.secondFormGroup.value.phoneNumber,
        "landline": this.secondFormGroup.value.phoneNumber,
        "userType": persona + "_ADMIN",
        "color": "", "address": "", "city": "", "state": "",
        "country": this.firstFormGroup.value.region,
        "zip": "", "inactive": false, "orgId": "", "upiId": "",
        "panCardNumber": "", "gender": "", "photoPath": "", "aadhaarCardNumber": ""
      },
      "global": this.firstFormGroup.value.category,
      "orgSubType": this.firstFormGroup.value.orgSubType || ''
    };

    return this.adminService.createOrg(data);
  }

  clear() {
    this.firstFormGroup.reset({ primaryPersona: 'buyer', termsAccepted: false });
    this.secondFormGroup.reset();
    this.submitted = false;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  landingPage() {
    this.router.navigate(['/']);
  }
  showSuccessDialogComponent(title: string, message: string, icon: string) {
    const dialog = this._dialog.open(SuccessDialog, {
      data: {
        title: title,
        subtitle: message,
        icon: icon
      },
      height: '400px',
      width: '500px',
      panelClass: 'custom-dialog-container'
    })
    dialog.afterClosed().subscribe((result: any) => {
      if (this.status == 'success') {
        if (this.dialogRef) {
          this.dialogRef.close(true);
        } else {
          this.router.navigate(['/login']);
        }
      } else {
        this.currentStep = 2;
      }
    })
  }

  viewHelp() {
    const helpDialog = this.dialog.open(Help, {
      disableClose: true,
      height: '500px',
      width: '500px',
      panelClass: 'custom-dialog-container',
      hasBackdrop: true,
      autoFocus: false

    });

    helpDialog.afterClosed().subscribe({

    })
  }

}
